import React, { useState, useEffect, useRef } from 'react';
import { useData } from '../context/DataContext';
import { usePet } from '../context/PetContext';
import { Medication } from '../types';
import { Plus, X, Clock, Bell, BellOff, GripVertical, MoreVertical, Pencil, Trash2, Copy, ChevronLeft, ChevronRight, ChevronDown, CalendarDays } from 'lucide-react';
import { Reorder, useDragControls } from 'motion/react';
import { cn } from '../lib/utils';
import { formatTime, timeToMinutes } from '../lib/timeUtils';
import { useSettings } from '../functions/useSettings';
import { addDays, addMonths, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, parseISO, startOfMonth, startOfWeek, subMonths } from 'date-fns';
import { Modal } from '../components/Modal';
import { TabSelector } from '../components/TabSelector';
import { ThemedDateField, formatDateKeyboardInput, isValidDateString } from '../components/ThemedDateField';
import { useDeleteConfirm } from '../hooks/useDeleteConfirm';

// Module-level guard: persists across Meds unmount/remount so the one-time
// timeslot-split migration never runs twice in a session.
const _migratedMedicationIds = new Set<string>();
import { useLanguage } from '../functions/useLanguage';
import { getDateLocale } from '../functions/getDateLocale';
import { formatDateByPreference } from '../functions/formatDateByPreference';
import { useToast } from '../hooks/useToast';
import { PillShape } from '../components/Custom Icons/PillShapeIcon';

function formatTimeKeyboardInput(raw: string) {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

function isValidTimeString(value: string) {
  return /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value);
}

function ThemedTimeField({
  value,
  onChange,
  use24h,
}: {
  value: string;
  onChange: (value: string) => void;
  use24h?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const timeOptions = Array.from({ length: 96 }, (_, index) => {
    const hour = Math.floor(index / 4).toString().padStart(2, '0');
    const minute = ((index % 4) * 15).toString().padStart(2, '0');
    return `${hour}:${minute}`;
  });

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={popoverRef}>
      <div className="flex items-center rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-highlight-dark transition-colors">
        <input
          type="text"
          inputMode="numeric"
          value={value}
          onChange={(event) => onChange(formatTimeKeyboardInput(event.target.value))}
          placeholder="HH:mm"
          className="flex-1 bg-transparent px-3 py-2.5 text-sm text-text-primary outline-none placeholder:text-text-secondary/50"
        />
        <button
          type="button"
          onClick={() => setOpen(current => !current)}
          className="mr-1.5 flex h-8 w-8 items-center justify-center rounded-md text-text-secondary hover:bg-gray-100"
          aria-label="Open time picker"
        >
          <Clock size={16} />
        </button>
      </div>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 w-full min-w-[140px] rounded-2xl border border-gray-200 bg-white shadow-xl p-1 max-h-60 overflow-y-auto">
          {timeOptions.map(option => (
            <button
              key={option}
              type="button"
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
              className={cn(
                'w-full rounded-lg px-3 py-2 text-left text-sm transition-colors',
                option === value
                  ? 'bg-primary-brown text-white font-semibold'
                  : 'text-text-primary hover:bg-gray-100'
              )}
            >
              {formatTime(option, use24h)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DosageUnitPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const units = ['mg', 'mcg', 'g', 'ml', 'units'];

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 h-full px-3 py-2.5 border rounded-xl border-gray-300 bg-white text-sm text-text-primary focus:ring-2 focus:ring-highlight-dark outline-none transition-colors min-w-[72px] justify-between"
      >
        <span>{value}</span>
        <ChevronDown size={14} className="text-text-secondary" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-28 rounded-2xl border border-gray-200 bg-white shadow-xl p-1">
          {units.map(u => (
            <button
              key={u}
              type="button"
              onClick={() => { onChange(u); setOpen(false); }}
              className={cn(
                'w-full rounded-lg px-3 py-2 text-left text-sm transition-colors',
                u === value ? 'bg-primary-brown text-white font-semibold' : 'text-text-primary hover:bg-gray-100'
              )}
            >
              {u}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


export default function Meds() {
  const { medications, addMedication, deleteMedication, updateMedication } = useData();
  const { isActivePetSubordinate } = usePet();
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addForTime, setAddForTime] = useState<string | null>(null);

  const openAddModal = (time?: string) => {
    setAddForTime(time ?? null);
    setIsModalOpen(true);
  };
  const [editingMed, setEditingMed] = useState<Medication | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [calendarMonth, setCalendarMonth] = useState(startOfMonth(new Date()));
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const { use24h } = useSettings();
  const { requestDelete, modal: deleteModal } = useDeleteConfirm();
  const migratedMedicationIds = useRef<Set<string>>(_migratedMedicationIds);

  // Cross-group drag support
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [draggingFromTime, setDraggingFromTime] = useState<string | null>(null);

  const handleDuplicate = (med: Medication) => {
    const { id: _id, ...rest } = med;
    addMedication({ ...rest, timeslots: [med.timeslots?.[0] ?? '08:00'] });
  };

  useEffect(() => {
    const medsNeedingSplit = medications.filter(
      med => (med.timeslots?.length ?? 0) > 1 && !migratedMedicationIds.current.has(med.id)
    );

    if (medsNeedingSplit.length === 0) return;

    medsNeedingSplit.forEach(med => migratedMedicationIds.current.add(med.id));

    void (async () => {
      for (const med of medsNeedingSplit) {
        const [firstTime, ...remainingTimes] = med.timeslots;
        await updateMedication(med.id, { timeslots: [firstTime] });

        for (const time of remainingTimes) {
          const { id: _ignoredId, ...rest } = med;
          await addMedication({ ...rest, timeslots: [time] });
        }
      }
    })();
  }, [addMedication, medications, updateMedication]);

  const handleDragStart = (fromTime: string) => {
    setDraggingFromTime(fromTime);
  };

  const handleDragEnd = (medId: string, fromTime: string, clientX: number, clientY: number) => {
    setDraggingFromTime(null);
    for (const [time, el] of Object.entries(groupRefs.current)) {
      if (time === fromTime || !el) continue;
      const rect = el.getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
        const med = medications.find(m => m.id === medId);
        if (!med) return;
        updateMedication(medId, { timeslots: [time] });
        return;
      }
    }
  };

  // Group medications by their timeslots
  const groupedByTime: Record<string, Medication[]> = {};
  medications.forEach(med => {
    // Ensure timeslots exists (backwards compatibility)
    const timeslots = med.timeslots || ['08:00'];
    timeslots.forEach(time => {
      if (!groupedByTime[time]) {
        groupedByTime[time] = [];
      }
      groupedByTime[time].push(med);
    });
  });

  // Sort times chronologically
  const sortedTimes = Object.keys(groupedByTime).sort(
    (a, b) => timeToMinutes(a) - timeToMinutes(b)
  );

  const handleAddMed = (med: Omit<Medication, 'id'>) => {
    addMedication(med);
    setIsModalOpen(false);
  };

  const handleEditMed = (med: Medication) => {
    setEditingMed(med);
  };

  const handleSaveEdit = (updates: Omit<Medication, 'id'>) => {
    if (editingMed) {
      updateMedication(editingMed.id, updates);
      setEditingMed(null);
    }
  };

  const handleDelete = (id: string, timeslot: string) => {
    const med = medications.find(m => m.id === id);
    if (!med) return;

    requestDelete(
      t('meds.removeMedication'),
      t('meds.removeConfirm'),
      () => deleteMedication(id)
    );
  };

  const handleToggleReminder = (id: string, currentState: boolean) => {
    updateMedication(id, { reminders_enabled: !currentState });
  };

  const isMedActiveOnDate = (med: Medication, dateStr: string) => {
    const start = med.treatment_start;
    const end = med.treatment_end;
    if (start && dateStr < start) return false;
    if (end && dateStr > end) return false;
    return true;
  };

  const getDueMedsForDate = (dateStr: string) =>
    medications
      .filter(med => isMedActiveOnDate(med, dateStr))
      .sort((a, b) => timeToMinutes(a.timeslots[0] || '00:00') - timeToMinutes(b.timeslots[0] || '00:00'));

  const monthStart = startOfWeek(startOfMonth(calendarMonth), { weekStartsOn: 1 });
  const monthEnd = endOfWeek(endOfMonth(calendarMonth), { weekStartsOn: 1 });
  const calendarDays: Date[] = [];
  let cursor = monthStart;
  while (cursor <= monthEnd) {
    calendarDays.push(cursor);
    cursor = addDays(cursor, 1);
  }

  const selectedDueMeds = getDueMedsForDate(selectedCalendarDate);

  return (
    <div className="w-full bg-white" data-tour="meds-list">

      <div className="space-y-6">
        <div className="sticky top-0 z-20 bg-white pt-2 pb-2 mb-4">
          <div className="relative flex items-center justify-center">
          <TabSelector
            tabs={[
              { value: 'list', label: 'List' },
              { value: 'calendar', label: 'Calendar' },
            ]}
            value={viewMode}
            onChange={(v) => setViewMode(v)}
          />

          {!isActivePetSubordinate && (
            <button
              onClick={() => openAddModal()}
              className="absolute right-0 flex items-center justify-center p-2 rounded-xl bg-primary-brown text-white hover:bg-hover-dark transition-colors"
            >
              <Plus size={20} />
            </button>
          )}
          </div>
        </div>

        {viewMode === 'list' ? (
          sortedTimes.length === 0 ? (
            <div className="space-y-4 select-none pointer-events-none opacity-50">
              {/* Skeleton med groups — mirrors real MedGroup + MedCard structure */}
              {[
                {
                  time: '8:00 AM',
                  cards: [
                    { color: '#4f9cf9', shape: 'round1' as const, name: 'Morning Medication', dose: '5mg' },
                    { color: '#f97316', shape: 'capsule2' as const, name: 'Daily Supplement', dose: '250mg' },
                  ],
                },
                {
                  time: '8:00 PM',
                  cards: [
                    { color: '#a855f7', shape: 'capsule1' as const, name: 'Evening Medication', dose: '10mg' },
                  ],
                },
              ].map(({ time, cards }) => (
                <div key={time} className="rounded-2xl p-4 bg-white">
                  {/* Group header — mirrors MedGroup header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-text-secondary" />
                      <h3 className="text-lg font-bold text-text-primary">{time}</h3>
                    </div>
                    <span className="text-sm font-medium px-2 py-1 rounded bg-gray-100 text-text-secondary">
                      {cards.length} {cards.length !== 1 ? t('meds.medsCount') : t('meds.medCount')}
                    </span>
                  </div>
                  {/* Med cards — mirrors MedCard layout */}
                  <div className="space-y-3">
                    {cards.map((card, i) => (
                      <div key={i} className="relative flex items-center justify-between p-3 rounded-xl border bg-white border-gray-100">
                        {/* Drag handle */}
                        <div className="mr-1 flex items-center self-stretch px-0.5">
                          <GripVertical size={18} className="text-gray-200" />
                        </div>
                        <div className="flex items-center gap-3 flex-1">
                          <PillShape color={card.color} shape={card.shape} />
                          <div className="flex-1 flex flex-col gap-0.5">
                            <span className="font-bold text-sm leading-tight text-text-primary">{card.name}</span>
                            <span className="text-xs leading-tight text-text-secondary">{card.dose}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="p-2 rounded text-yellow-400"><Bell size={16} /></div>
                          <div className="p-2 rounded text-gray-300"><MoreVertical size={16} /></div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Add button */}
                  <div className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-gray-200 text-gray-300 text-sm font-medium">
                    <Plus size={16} />
                    {t('meds.addMedication')}
                  </div>
                </div>
              ))}
              <div className="flex flex-col items-center gap-3 pt-6 pb-8 text-center pointer-events-none" style={{ opacity: 1 }}>
                <p className="text-base font-bold text-text-primary">{t('meds.empty')}</p>
                <p className="text-sm text-text-secondary">Tap &ldquo;Add Medication&rdquo; above to get started</p>
              </div>
            </div>
          ) : (
            sortedTimes.map((time) => (
              <MedGroup
                key={time}
                time={time}
                meds={groupedByTime[time]}
                onDelete={(id) => handleDelete(id, time)}
                onEdit={handleEditMed}
                onDuplicate={handleDuplicate}
                onToggleReminder={handleToggleReminder}
                use24h={use24h}
                isDragTarget={draggingFromTime !== null && draggingFromTime !== time}
                containerRef={(el) => { groupRefs.current[time] = el; }}
                onDragStart={() => handleDragStart(time)}
                onDragEnd={(medId, clientX, clientY) => handleDragEnd(medId, time, clientX, clientY)}
                onAddMed={() => openAddModal(time)}
                isSubordinate={isActivePetSubordinate}
              />
            ))
          )
        ) : (
          <div className="space-y-4">
            <div className="rounded-2xl border border-gray-100 bg-white p-3">
              <div className="flex items-center justify-between mb-3 px-1">
                <button
                  onClick={() => setCalendarMonth(m => subMonths(m, 1))}
                  className="p-2 rounded-full hover:bg-white text-text-secondary"
                >
                  ‹
                </button>
                <h3 className="font-bold text-text-primary">{format(calendarMonth, 'MMMM yyyy')}</h3>
                <button
                  onClick={() => setCalendarMonth(m => addMonths(m, 1))}
                  className="p-2 rounded-full hover:bg-white text-text-secondary"
                >
                  ›
                </button>
              </div>

              <div className="grid grid-cols-7 border-t border-l border-gray-100 mb-0">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(wd => (
                  <div key={wd} className="text-center text-[11px] font-bold uppercase tracking-wide py-1 text-gray-400 border-r border-b border-gray-100">
                    {wd}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 border-l border-gray-100">
                {calendarDays.map(date => {
                  const key = format(date, 'yyyy-MM-dd');
                  const dueCount = getDueMedsForDate(key).reduce((sum, med) => sum + (med.timeslots?.length || 0), 0);
                  const isSelected = selectedCalendarDate === key;
                  const inMonth = isSameMonth(date, calendarMonth);
                  const isTodayCell = isSameDay(date, new Date());
                  return (
                    <button
                      key={key}
                      onClick={() => setSelectedCalendarDate(key)}
                      disabled={!inMonth}
                      className={cn(
                        'h-14 p-1.5 text-left flex flex-col justify-between transition-colors border-r border-b border-gray-100',
                        !inMonth && 'opacity-35 cursor-default',
                        isSelected ? 'bg-primary-brown text-white' : 'bg-white hover:bg-primary-brown/5',
                        isTodayCell && !isSelected && 'ring-inset ring-1 ring-primary-brown/30'
                      )}
                    >
                      <span className={cn('text-xs font-bold', isSelected ? 'text-white' : 'text-text-primary')}>
                        {format(date, 'd')}
                      </span>
                      <span className={cn(
                        'text-[10px] font-semibold px-1.5 py-0.5 rounded-md self-start',
                        isSelected ? 'bg-white/20 text-white' : 'bg-primary-brown/10 text-primary-brown'
                      )}>
                        {dueCount} due
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-4">
              <h4 className="font-bold text-text-primary mb-3">
                {format(parseISO(selectedCalendarDate), 'EEEE, MMM d')} · {selectedDueMeds.length} medication{selectedDueMeds.length === 1 ? '' : 's'}
              </h4>
              {selectedDueMeds.length === 0 ? (
                <p className="text-sm text-gray-400">No medications due this day.</p>
              ) : (
                <div className="space-y-2">
                  {selectedDueMeds.map(med => (
                    <div key={med.id} className="rounded-xl border border-gray-100 bg-white px-3 py-2">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-bold text-text-primary">{med.name}</p>
                          <p className="text-xs text-text-secondary">{med.dosage_mg} · {med.timeslots.map(t => formatTime(t, use24h)).join(', ')}</p>
                        </div>
                        <PillShape color={med.color_hex} shape={med.shape_type} size={28} />
                      </div>
                      {(med.treatment_start || med.treatment_end) && (
                        <p className="mt-1 text-[11px] text-text-secondary">
                          Period: {med.treatment_start || '—'} → {med.treatment_end || 'ongoing'}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

      {isModalOpen && (
        <MedModal onClose={() => { setIsModalOpen(false); setAddForTime(null); }} onSave={handleAddMed} initialTime={addForTime ?? undefined} />
      )}
      {editingMed && (
        <MedModal onClose={() => setEditingMed(null)} onSave={handleSaveEdit} initial={editingMed} />
      )}
      {deleteModal}
      </div>{/* end content */}
    </div>
  );
}

/** Returns `meds` sorted by the persisted order stored in localStorage. */
function reconcileOrder(time: string, meds: Medication[]): Medication[] {
  const stored: Record<string, string[]> = JSON.parse(localStorage.getItem('koda_med_order') || '{}');
  const storedIds: string[] = stored[time] || [];
  if (storedIds.length === 0) return meds;
  const medMap = new Map(meds.map(m => [m.id, m]));
  const result: Medication[] = [];
  for (const id of storedIds) {
    if (medMap.has(id)) result.push(medMap.get(id)!);
  }
  for (const med of meds) {
    if (!storedIds.includes(med.id)) result.push(med);
  }
  return result;
}

interface MedGroupProps {
  time: string
  meds: Medication[]
  onDelete: (id: string) => void
  onEdit: (med: Medication) => void
  onDuplicate: (med: Medication) => void
  onToggleReminder: (id: string, currentState: boolean) => void
  use24h?: boolean
  isDragTarget?: boolean
  containerRef?: (el: HTMLDivElement | null) => void
  onDragStart?: () => void
  onDragEnd?: (medId: string, clientX: number, clientY: number) => void
  onAddMed?: () => void
  isSubordinate?: boolean
}

const MedGroup: React.FC<MedGroupProps> = ({ 
  time, 
  meds, 
  onDelete,
  onEdit,
  onDuplicate,
  onToggleReminder,
  use24h,
  isDragTarget,
  containerRef,
  onDragStart,
  onDragEnd,
  onAddMed,
  isSubordinate,
}) => {
  const { t } = useLanguage();
  const [orderedMeds, setOrderedMeds] = useState<Medication[]>(() => reconcileOrder(time, meds));

  // Reconcile when medications are added or removed
  useEffect(() => {
    setOrderedMeds(reconcileOrder(time, meds));
  }, [meds, time]);

  const handleReorder = (newOrder: Medication[]) => {
    setOrderedMeds(newOrder);
    const stored: Record<string, string[]> = JSON.parse(localStorage.getItem('koda_med_order') || '{}');
    stored[time] = newOrder.map(m => m.id);
    localStorage.setItem('koda_med_order', JSON.stringify(stored));
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "rounded-2xl p-4 bg-white transition-all",
        isDragTarget && "ring-2 ring-highlight-dark ring-offset-1"
      )}
    >
      {isDragTarget && (
        <p className="text-xs font-medium text-text-secondary text-center mb-2 -mt-1">
          {t('meds.dropHere')}
        </p>
      )}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-text-secondary" />
          <h3 className="text-lg font-bold text-text-primary">{formatTime(time, use24h)}</h3>
        </div>
        <span className="text-sm font-medium px-2 py-1 rounded bg-gray-100 text-text-secondary">
          {meds.length} {meds.length !== 1 ? t('meds.medsCount') : t('meds.medCount')}
        </span>
      </div>
      <Reorder.Group axis="y" values={orderedMeds} onReorder={handleReorder} as="div" className="space-y-3">
        {orderedMeds.map(med => (
          <SortableMedCard
            key={med.id}
            med={med}
            onDelete={onDelete}
            onEdit={onEdit}
            onDuplicate={onDuplicate}
            onToggleReminder={onToggleReminder}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            isSubordinate={isSubordinate}
          />
        ))}
      </Reorder.Group>
      {!isSubordinate && (
        <button
          onClick={onAddMed}
          className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-gray-300 text-text-secondary text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          <Plus size={16} />
          {t('meds.addMedication')}
        </button>
      )}
    </div>
  );
};

interface MedCardProps {
  med: Medication
  onDelete: (id: string) => void
  onEdit: (med: Medication) => void
  onDuplicate: (med: Medication) => void
  onToggleReminder: (id: string, currentState: boolean) => void
  dragControls?: ReturnType<typeof useDragControls>
  isSubordinate?: boolean
}

const MedCard: React.FC<MedCardProps> = ({
  med,
  onDelete,
  onEdit,
  onDuplicate,
  onToggleReminder,
  dragControls,
  isSubordinate,
}) => {
  const { t } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  return (
    <div
      className={cn(
        "relative group flex items-center justify-between p-3 rounded-xl border transition-colors",
        "bg-white border-gray-100"
      )}
    >
      {/* Drag handle — only this initiates dragging */}
      <div
        onPointerDown={dragControls ? e => { e.stopPropagation(); dragControls.start(e); } : undefined}
        className={cn(
          "mr-1 flex items-center self-stretch px-0.5 touch-none select-none",
          dragControls
            ? "cursor-grab active:cursor-grabbing text-gray-300"
            : "invisible pointer-events-none"
        )}
      >
        <GripVertical size={18} />
      </div>

      <div className="flex items-center gap-3 flex-1">
        <PillShape color={med.color_hex} shape={med.shape_type} />
        <div className="flex-1 flex flex-col gap-0.5">
          <span className="font-bold text-sm leading-tight text-text-primary">{med.name}</span>
          <span className="text-xs leading-tight text-text-secondary">{med.dosage_mg}</span>
        </div>
      </div>
      
      <div
        className="flex items-center gap-1"
        onPointerDown={e => e.stopPropagation()}
      >
        {!isSubordinate && (
          <>
            <button
              onClick={() => onToggleReminder(med.id, med.reminders_enabled ?? true)}
              className={cn("p-2 rounded transition-colors cursor-pointer", (med.reminders_enabled ?? true) ? "text-yellow-500 hover:bg-yellow-100" : "text-gray-400 hover:bg-gray-200")}
              title={(med.reminders_enabled ?? true) ? t('meds.remindersEnabled') : t('meds.remindersDisabled')}
            >
              {(med.reminders_enabled ?? true) ? <Bell size={16} /> : <BellOff size={16} />}
            </button>
            
            {/* Kebab menu */}
            <div ref={menuRef} className="relative">
              <button
                onClick={() => setMenuOpen(v => !v)}
                className={cn("p-2 rounded-full transition-colors", "text-gray-400 hover:bg-gray-100")}
              >
                <MoreVertical size={18} />
              </button>

              {menuOpen && (
                <div className={cn(
                  "absolute right-0 top-full mt-1 w-36 rounded-xl shadow-lg border overflow-hidden z-50",
                  "bg-white border-gray-100"
                )}>
                  <button
                    onClick={() => { setMenuOpen(false); onEdit(med); }}
                    className={cn(
                      "w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium transition-colors",
                      "text-text-secondary hover:bg-gray-50"
                    )}
                  >
                    <Pencil size={15} />
                    {t('meds.edit')}
                  </button>
                  <button
                    onClick={() => { setMenuOpen(false); onDuplicate(med); }}
                    className={cn(
                      "w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium transition-colors border-t",
                      "text-text-secondary hover:bg-gray-50 border-gray-100"
                    )}
                  >
                    <Copy size={15} />
                    {t('meds.duplicate')}
                  </button>
                  <button
                    onClick={() => { setMenuOpen(false); onDelete(med.id); }}
                    className={cn(
                      "w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium transition-colors border-t",
                      "text-error-red hover:bg-error-red\/5 border-gray-100"
                    )}
                  >
                    <Trash2 size={15} />
                    {t('meds.delete')}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/** Wraps MedCard in a Reorder.Item with its own drag controls. */
const SortableMedCard: React.FC<{
  med: Medication;
  onDelete: (id: string) => void;
  onEdit: (med: Medication) => void;
  onDuplicate: (med: Medication) => void;
  onToggleReminder: (id: string, currentState: boolean) => void;
  onDragStart?: () => void;
  onDragEnd?: (medId: string, clientX: number, clientY: number) => void;
  isSubordinate?: boolean;
}> = ({ med, onDelete, onEdit, onDuplicate, onToggleReminder, onDragStart, onDragEnd, isSubordinate }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={med}
      dragListener={false}
      dragControls={controls}
      as="div"
      className="rounded-xl"
      initial={false}
      animate={{ scale: 1, boxShadow: '0 0 0px rgba(0,0,0,0)', zIndex: 0 }}
      whileDrag={{ scale: 1.02, boxShadow: '0 8px 24px rgba(0,0,0,0.13)', zIndex: 50 }}
      transition={{ duration: 0.15 }}
      onDragStart={() => onDragStart?.()}
      onDragEnd={(_event, _info) => {
        const e = _event as PointerEvent;
        onDragEnd?.(med.id, e.clientX ?? 0, e.clientY ?? 0);
      }}
    >
      <MedCard
        med={med}
        onDelete={onDelete}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onToggleReminder={onToggleReminder}
        dragControls={isSubordinate ? undefined : controls}
        isSubordinate={isSubordinate}
      />
    </Reorder.Item>
  );
}

const POST_TRANSPLANT_MEDS = [
  // ── Oral medications ────────────────────────────────────────────────────
  // Kidney support / renal
  'Benazepril', 'Enalapril', 'Amlodipine', 'Telmisartan', 'Ramipril',
  'Furosemide', 'Spironolactone', 'Hydrochlorothiazide',
  'Lisinopril', 'Losartan', 'Torsemide',
  // CKD mineral & electrolyte management
  'Calcitriol', 'Sodium Bicarbonate', 'Sevelamer',
  'Cinacalcet', 'Paricalcitol',
  // Erythropoiesis-stimulating agents (CKD anaemia)
  'Darbepoetin Alfa', 'Epoetin Alfa',
  // Immunosuppressants (post-transplant cats/dogs)
  'Tacrolimus', 'Cyclosporine', 'Prednisolone', 'Chlorambucil', 'Mycophenolate Mofetil',
  // Anti-nausea / GI
  'Maropitant (Cerenia)', 'Metoclopramide', 'Omeprazole', 'Famotidine',
  'Sucralfate', 'Metronidazole', 'Tylosin', 'Probiotics',
  // Antibiotics (oral)
  'Amoxicillin', 'Amoxicillin-Clavulanate (Clavulox)', 'Doxycycline',
  'Enrofloxacin (Baytril)', 'Marbofloxacin', 'Clindamycin',
  'Trimethoprim-Sulfa', 'Cephalexin', 'Azithromycin',
  // Pain / anti-inflammatory (oral)
  'Meloxicam (Metacam)', 'Robenacoxib (Onsior)', 'Carprofen (Rimadyl)',
  'Gabapentin', 'Tramadol', 'Pregabalin',
  // Thyroid (cats — oral)
  'Methimazole (Felimazole)', 'Carbimazole',
  // Cardiac (oral)
  'Atenolol', 'Digoxin', 'Pimobendan (Vetmedin)', 'Diltiazem', 'Sotalol',
  'Fludrocortisone', 'Sildenafil',
  // Phosphate binders / CKD support (oral)
  'Aluminium Hydroxide', 'Lanthanum Carbonate', 'Calcium Carbonate',
  'Ipakitine', 'Epakitin', 'Azodyl', 'Semintra (Telmisartan oral solution)',
  // Vitamins & supplements (oral)
  'Vitamin B12 (Cobalamin)', 'Vitamin K1', 'Omega-3 Fish Oil',
  'Iron Supplement', 'Potassium Gluconate', 'Phosphorus Supplement',
  // Steroids (oral)
  'Dexamethasone', 'Hydrocortisone', 'Methylprednisolone',
  // Antifungals (oral)
  'Itraconazole', 'Fluconazole', 'Ketoconazole',
  // Behaviour / neuro (oral)
  'Phenobarbital', 'Potassium Bromide', 'Trazodone', 'Acepromazine',
  'Fluoxetine', 'Clomipramine',
  // Miscellaneous oral
  'Ursodiol (Ursodeoxycholic Acid)', 'Propranolol', 'Atropine',
  'Lactulose', 'Misoprostol', 'Colchicine',
  // Oral antiparasitics / dewormers
  'Fenbendazole (Panacur)', 'Praziquantel (Droncit)', 'Milbemycin Oxime',
  'Pyrantel', 'Oxibendazole',
  // Oral flea / tick / parasite prevention
  'Afoxolaner (NexGard)', 'Fluralaner (Bravecto Chew)', 'Sarolaner (Simparica)',
  'Lotilaner (Credelio)', 'Spinosad (Comfortis)',
  // ── Topical / spot-on treatments ────────────────────────────────────────
  // Flea & tick spot-ons
  'Fipronil (Frontline)', 'Fipronil + S-Methoprene (Frontline Plus)',
  'Selamectin (Revolution / Stronghold)', 'Imidacloprid (Advantage)',
  'Imidacloprid + Moxidectin (Advocate / Advantage Multi)',
  'Fluralaner (Bravecto Spot-On)', 'Permethrin (Advantix — dogs only)',
  'Dinotefuran + Permethrin (Vectra 3D — dogs only)',
  // Topical antibiotics / antifungals
  'Chlorhexidine Solution', 'Silver Sulfadiazine Cream', 'Miconazole Spray',
  'Clotrimazole Cream', 'Mupirocin Ointment',
  // Topical pain / anti-inflammatory
  'Diclofenac Gel', 'Fentanyl Patch', 'Lidocaine Patch',
  // Ear treatments (topical)
  'Otomax (Ear Drops)', 'Surolan (Ear Drops)', 'Osurnia (Ear Gel)',
  'Claro (Florfenicol Ear)', 'Zymox Ear Solution',
  // Eye drops / ointments
  'Atropine Eye Drops', 'Prednisolone Eye Drops', 'Ofloxacin Eye Drops',
  'Tobramycin Eye Drops', 'Hyaluronate Eye Drops',
  // Skin / wound
  'Neosporin (Topical)', 'Betadine Solution', 'Aluminium Hydroxide Gel',
  // Topical thyroid (cats)
  'Methimazole Transdermal Gel',
];

// Maps lowercase keyword substrings → palette hex. First match wins.
const MED_COLOR_MAP: Array<{ keyword: string; hex: string }> = [
  // Beige/Buff — Phenobarbital, Potassium Bromide
  { keyword: 'phenobarbital',          hex: '#D4B896' },
  { keyword: 'potassium bromide',       hex: '#D4B896' },
  // Pink — palatable chewables, amoxicillin-family, maropitant, flea chews, carprofen, cephalexin
  { keyword: 'amoxicillin',             hex: '#F4A7B9' },
  { keyword: 'clavulox',                hex: '#F4A7B9' },
  { keyword: 'clavulanate',             hex: '#F4A7B9' },
  { keyword: 'maropitant',              hex: '#F4A7B9' },
  { keyword: 'cerenia',                 hex: '#F4A7B9' },
  { keyword: 'nexgard',                 hex: '#F4A7B9' },
  { keyword: 'afoxolaner',              hex: '#F4A7B9' },
  { keyword: 'bravecto chew',           hex: '#F4A7B9' },
  { keyword: 'fluralaner (bravecto c',  hex: '#F4A7B9' },
  { keyword: 'simparica',               hex: '#F4A7B9' },
  { keyword: 'sarolaner',               hex: '#F4A7B9' },
  { keyword: 'credelio',                hex: '#F4A7B9' },
  { keyword: 'lotilaner',               hex: '#F4A7B9' },
  { keyword: 'comfortis',               hex: '#F4A7B9' },
  { keyword: 'spinosad',                hex: '#F4A7B9' },
  { keyword: 'carprofen',               hex: '#F4A7B9' },
  { keyword: 'rimadyl',                 hex: '#F4A7B9' },
  { keyword: 'cephalexin',              hex: '#F4A7B9' },
  // Yellow/Cream — steroids, gabapentin/pregabalin, vitamins & supplements
  { keyword: 'prednisolone',            hex: '#F5E6A0' },
  { keyword: 'dexamethasone',           hex: '#F5E6A0' },
  { keyword: 'methylprednisolone',      hex: '#F5E6A0' },
  { keyword: 'hydrocortisone',          hex: '#F5E6A0' },
  { keyword: 'gabapentin',              hex: '#F5E6A0' },
  { keyword: 'pregabalin',              hex: '#F5E6A0' },
  { keyword: 'vitamin b12',             hex: '#F5E6A0' },
  { keyword: 'cobalamin',               hex: '#F5E6A0' },
  { keyword: 'vitamin k1',              hex: '#F5E6A0' },
  { keyword: 'omega-3',                 hex: '#F5E6A0' },
  { keyword: 'fish oil',                hex: '#F5E6A0' },
  { keyword: 'potassium gluconate',     hex: '#F5E6A0' },
  { keyword: 'ipakitine',               hex: '#F5E6A0' },
  { keyword: 'epakitin',                hex: '#F5E6A0' },
  { keyword: 'azodyl',                  hex: '#F5E6A0' },
  // Orange — iron, some antiparasitics
  { keyword: 'iron supplement',         hex: '#F97316' },
  { keyword: 'pyrantel',                hex: '#F97316' },
  { keyword: 'milbemycin',              hex: '#F97316' },
  { keyword: 'oxibendazole',            hex: '#F97316' },
  // Blue — sedatives, acepromazine
  { keyword: 'acepromazine',            hex: '#4D98E2' },
  { keyword: 'trazodone',               hex: '#4D98E2' },
  // Green — dewormers, antifungals, probiotics
  { keyword: 'fenbendazole',            hex: '#52C12E' },
  { keyword: 'panacur',                 hex: '#52C12E' },
  { keyword: 'praziquantel',            hex: '#52C12E' },
  { keyword: 'droncit',                 hex: '#52C12E' },
  { keyword: 'itraconazole',            hex: '#52C12E' },
  { keyword: 'probiotics',              hex: '#52C12E' },
  // Brown/Tan — pimobendan, robenacoxib, lactulose
  { keyword: 'pimobendan',              hex: '#A0724A' },
  { keyword: 'vetmedin',                hex: '#A0724A' },
  { keyword: 'robenacoxib',             hex: '#A0724A' },
  { keyword: 'onsior',                  hex: '#A0724A' },
  { keyword: 'lactulose',               hex: '#A0724A' },
  // Red — heart meds, digoxin, sotalol, diltiazem, sildenafil
  { keyword: 'digoxin',                 hex: '#D94040' },
  { keyword: 'sotalol',                 hex: '#D94040' },
  { keyword: 'diltiazem',               hex: '#D94040' },
  { keyword: 'sildenafil',              hex: '#D94040' },
  // Purple — fluoxetine, clomipramine, cyclosporine, tacrolimus
  { keyword: 'fluoxetine',              hex: '#9B59B6' },
  { keyword: 'clomipramine',            hex: '#9B59B6' },
  { keyword: 'cyclosporine',            hex: '#9B59B6' },
  { keyword: 'tacrolimus',              hex: '#9B59B6' },
  // Yellow/Cream — calcitriol (vitamin D analogue), paricalcitol
  { keyword: 'calcitriol',              hex: '#F5E6A0' },
  { keyword: 'paricalcitol',            hex: '#F5E6A0' },
  // Orange — darbepoetin, epoetin (haematopoietic agents)
  { keyword: 'darbepoetin',             hex: '#F97316' },
  { keyword: 'epoetin',                 hex: '#F97316' },
  // White (default) — lisinopril, losartan, torsemide, sodium bicarbonate, sevelamer, cinacalcet
  { keyword: 'lisinopril',              hex: '#F5F5F5' },
  { keyword: 'losartan',                hex: '#F5F5F5' },
  { keyword: 'torsemide',               hex: '#F5F5F5' },
  { keyword: 'sodium bicarbonate',      hex: '#F5F5F5' },
  { keyword: 'sevelamer',               hex: '#F5F5F5' },
  { keyword: 'cinacalcet',              hex: '#F5F5F5' },
];

/** Returns a palette hex for a known medication name, or null if unrecognised. */
function inferMedColor(medName: string): string | null {
  const lower = medName.trim().toLowerCase();
  if (!lower) return null;
  // Only infer when the name exactly matches a suggestion in the list
  const isKnown = POST_TRANSPLANT_MEDS.some(m => m.toLowerCase() === lower);
  if (!isKnown) return null;
  const match = MED_COLOR_MAP.find(({ keyword }) => lower.includes(keyword));
  return match ? match.hex : null;
}

function MedModal({ onClose, onSave, initial, initialTime }: { onClose: () => void, onSave: (med: Omit<Medication, 'id'>) => void, initial?: Medication, initialTime?: string }) {
  const { t } = useLanguage();
  const { showToast, ToastMount } = useToast();
  const isEditing = !!initial;
  const { use24h } = useSettings();
  
  // Parse initial dosage if editing
  const parseDosage = (dosageStr: string) => {
    const match = dosageStr.match(/^([\d.]+)(.*)$/);
    return match ? { value: match[1], unit: match[2] || 'mg' } : { value: '', unit: 'mg' };
  };
  const initialDosage = initial ? parseDosage(initial.dosage_mg) : { value: '', unit: 'mg' };

  const [name, setName] = useState(initial?.name ?? '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const nameWrapperRef = useRef<HTMLDivElement>(null);
  const [dosage, setDosage] = useState(initialDosage.value);
  const [dosageUnit, setDosageUnit] = useState(initialDosage.unit);
  const [timeslot, setTimeslot] = useState<string>(initial?.timeslots?.[0] ?? initialTime ?? '08:00');
  const [treatmentStart, setTreatmentStart] = useState<string>(initial?.treatment_start ?? format(new Date(), 'yyyy-MM-dd'));
  const [treatmentEnd, setTreatmentEnd] = useState<string>(initial?.treatment_end ?? '');
  const [color, setColor] = useState(initial?.color_hex ?? '#F5F5F5');
  const [shape, setShape] = useState<Medication['shape_type']>(initial?.shape_type ?? 'round1');

  // Palette: real-world veterinary pill colours
  const PALETTE = [
    '#F5F5F5', // White — antibiotics, anti-inflammatories, general medications
    '#F5E6A0', // Yellow/Cream — vitamins, supplements, some steroids
    '#F4A7B9', // Pink — palatable chewables, some antibiotics (amoxicillin)
    '#F97316', // Orange — vitamins, some anti-parasitics
    '#4D98E2', // Blue — sedatives, some antihistamines
    '#52C12E', // Green — some dewormers, herbal supplements
    '#A0724A', // Brown/Tan — liver-flavoured chewables, joint supplements
    '#D94040', // Red — some heart medications, iron supplements
    '#9B59B6', // Purple — some specialty medications
    '#D4B896', // Beige/Buff — phenobarbital (seizure medication)
  ];

  const isCustomColor = !PALETTE.includes(color);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !dosage || !timeslot) {
      showToast(t('meds.fillAllFields'), 'warning');
      return;
    }

    if (!isValidDateString(treatmentStart)) {
      showToast('Please enter a valid start date', 'warning');
      return;
    }

    if (treatmentEnd && !isValidDateString(treatmentEnd)) {
      showToast('Please enter a valid end date', 'warning');
      return;
    }

    if (!isValidTimeString(timeslot)) {
      showToast('Please enter a valid time', 'warning');
      return;
    }

    if (treatmentEnd && treatmentEnd < treatmentStart) {
      showToast('End date cannot be before start date', 'warning');
      return;
    }

    onSave({
      user_id: 'current-user',
      name,
      dosage_mg: `${dosage}${dosageUnit}`,
      color_hex: color,
      shape_type: shape,
      timeslots: [timeslot],
      treatment_start: treatmentStart || undefined,
      treatment_end: treatmentEnd || undefined,
      reminders_enabled: true,
    });
  };

  return (
  <>
    <Modal
      open
      title={isEditing ? t('meds.editMedication') : t('meds.addMedication')}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 p-3 rounded-xl font-medium transition-colors text-text-secondary hover:bg-gray-100"
          >
            {t('meds.cancel')}
          </button>
          <button
            type="submit"
            form="add-med-form"
            className="flex-1 p-3 bg-primary-brown text-white rounded-xl font-bold hover:bg-hover-dark transition-colors shadow-sm"
          >
            {isEditing ? t('meds.saveChanges') : t('meds.addMedication')}
          </button>
        </>
      }
    >
      <form id="add-med-form" onSubmit={handleSubmit} className="space-y-4">
          <div ref={nameWrapperRef} className="relative">
            <label className="block text-sm font-medium mb-1 text-text-secondary">{t('meds.medicationName')}</label>
            <input
              required
              autoComplete="off"
              value={name}
              onChange={e => { setName(e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => {
                setTimeout(() => setShowSuggestions(false), 150);
                if (!isEditing) {
                  const inferred = inferMedColor(name);
                  if (inferred) setColor(inferred);
                }
              }}
              className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-highlight-dark outline-none border-gray-300 text-sm text-text-primary"
              placeholder={t('meds.medicationNamePlaceholder')}
            />
            {showSuggestions && (() => {
              const q = name.trim().toLowerCase();
              const matches = POST_TRANSPLANT_MEDS.filter(m =>
                q.length === 0 ? false : m.toLowerCase().includes(q)
              );
              return matches.length > 0 ? (
                <ul className="absolute z-50 left-0 right-0 top-full mt-1 rounded-xl border shadow-xl overflow-y-auto max-h-48 bg-white border-gray-200">
                  {matches.map(med => (
                    <li key={med}>
                      <button
                        type="button"
                        onMouseDown={() => { setName(med); setShowSuggestions(false); if (!isEditing) { const inferred = inferMedColor(med); if (inferred) setColor(inferred); } }}
                        className="w-full text-left px-4 py-2.5 text-sm transition-colors text-text-primary hover:bg-gray-50"
                      >
                        {med}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null;
            })()}
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-text-secondary">{t('meds.dosage')}</label>
            <div className="flex gap-2">
              <input
                required
                type="number"
                inputMode="decimal"
                min="0"
                step="any"
                value={dosage}
                onChange={e => setDosage(e.target.value)}
                className="flex-1 p-3 border rounded-xl focus:ring-2 focus:ring-highlight-dark outline-none border-gray-300 text-sm text-text-primary"
                placeholder={t('meds.dosagePlaceholder')}
              />
              <DosageUnitPicker value={dosageUnit} onChange={setDosageUnit} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">{t('meds.timeslots')}</label>
            <ThemedTimeField
              value={timeslot}
              onChange={setTimeslot}
              use24h={use24h}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-text-secondary">Treatment period</label>
            <div className="grid grid-cols-2 gap-2">
              <ThemedDateField
                label="Start"
                value={treatmentStart}
                onChange={setTreatmentStart}
              />
              <ThemedDateField
                label="End (optional)"
                value={treatmentEnd}
                onChange={setTreatmentEnd}
                allowClear
                placeholder="Ongoing"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-text-secondary">{t('meds.type')}</label>
            <div className="bg-white border border-gray-100 rounded-2xl p-2">
            <div className="grid grid-cols-4 gap-2">
              {([
                { id: 'capsule1', label: t('meds.shapeCapsule') },
                { id: 'capsule2', label: t('meds.shapeOval') },
                { id: 'capsule3', label: t('meds.shapeTwoPart') },
                { id: 'diamond',  label: t('meds.shapeDiamond') },
                { id: 'round1',   label: t('meds.shapeRound') },
                { id: 'round2',   label: t('meds.shapeFlat') },
                { id: 'triangle', label: t('meds.shapeTriangle') },
                { id: 'pipette', label: 'Pipette (Drops)' },
                { id: 'feedingsyringe', label: 'Feeding Syringe' },
              ] as { id: Medication['shape_type']; label: string }[]).map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setShape(id)}
                  className={cn(
                    "flex flex-col items-center gap-1 py-2 px-1 rounded-xl transition-all",
                    shape === id
                      ? "bg-white ring-2 ring-primary-brown shadow-sm"
                      : "hover:bg-white/70"
                  )}
                >
                  <PillShape color={color} shape={id} size={36} />
                  <span className={cn("text-xs font-medium leading-tight text-center", shape === id ? "text-primary-brown" : "text-text-secondary")}>{label}</span>
                </button>
              ))}
            </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-text-secondary">{t('meds.pillColour')}</label>
              <div className="flex items-center gap-2">
                {/* Live preview */}
                <PillShape color={color} shape={shape ?? 'round1'} />
                <span className="text-xs font-mono text-text-secondary">{color.toUpperCase()}</span>
              </div>
            </div>

            {/* Palette grid */}
            <div className="bg-white border border-gray-100 rounded-2xl p-2 mb-2">
            <div className="grid grid-cols-7 gap-1.5">
              {PALETTE.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  title={c}
                  className={cn(
                    "w-full aspect-square rounded-lg transition-transform hover:scale-110",
                    color === c
                      ? "ring-2 ring-highlight-dark ring-offset-1 scale-110"
                      : ""
                  )}
                  style={{ backgroundColor: c }}
                />
              ))}

              {/* Custom colour swatch */}
              <label
                title={t('meds.customColour')}
                className={cn(
                  "relative w-full aspect-square rounded-lg border flex items-center justify-center text-lg transition-transform hover:scale-110 cursor-pointer",
                  isCustomColor
                    ? "ring-2 ring-highlight-dark ring-offset-1 scale-110"
                    : "border-gray-200 bg-white"
                )}
                style={isCustomColor ? { backgroundColor: color } : {}}
              >
                {!isCustomColor && <span className="text-gray-400 text-base leading-none">＋</span>}
                {/* Keep the native colour input on-screen so its popup anchors correctly */}
                <input
                  type="color"
                  value={color}
                  onChange={e => setColor(e.target.value.toUpperCase())}
                  aria-label={t('meds.customColour')}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </label>
            </div>
            </div>
          </div>

      </form>
    </Modal>
    <ToastMount />
  </>
  );
}
